import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import InlineErrorMessage from '../../Components/InlineErrorMessage';
import AuthLayout from '../../Components/AuthLayout';
import { verifyEmailSignInOtp, verifySignupPhone, authenticateWithEmail, authenticateWithPhone } from '../../Services/auth';
import { getProducts, startSubscription, createDirectSubscription } from '../../Services/subscription';
import { handlePostLoginFlow } from '../../Utils/postLoginHandler';
import { getUserProfile } from '../../Services/MyProfile';
import { findPlanProduct } from '../../Utils/planUtils';
import { useHeader } from '../../Providers/HeaderContextProvider';
import './style.css';

const OtpVerificationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { addProfileData } = useHeader();
  
  const authData = location.state;
  
  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [timer, setTimer] = useState(60);
  const [isTimerActive, setIsTimerActive] = useState(true);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [products, setProducts] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);

  const planName = searchParams.get('plan');
  const subscriptionMode = searchParams.get('subscription_mode');
  // Share Aris invite token, preserved across the auth pages via the query
  // string (see EmailInputPage/PhoneInputPage) from /share/:token. Passed to the
  // verify call so the backend can activate a Companion Membership / Aris Pass.
  const shareToken = searchParams.get('share_token');

  // Redirect if no auth data
  useEffect(() => {
    if (!authData) {
      navigate('/login');
    }
  }, [authData, navigate]);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (isTimerActive && timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timer]);

  // Fetch products if marketing params present
  useEffect(() => {
    if (planName && subscriptionMode) {
      fetchProducts();
    }
  }, [planName, subscriptionMode]);

  const fetchProducts = async () => {
    try {
      const response = await getProducts();
      if (response?.payload?.products) {
        setProducts(response.payload.products);
        
        const planData = findPlanProduct(response.payload.products, planName, subscriptionMode);
        
        if (planData) {
          setPlanInfo({
            name: planData.displayName,
            price: planData.price,
            interval: planData.interval
          });
        }
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleOtpChange = (value, index) => {
    const digits = (value || '').replace(/\D/g, '');

    if (digits.length === 0) {
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      return;
    }

    if (digits.length === 1) {
      const newOtp = [...otp];
      newOtp[index] = digits;
      setOtp(newOtp);
      if (index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-input-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
      return;
    }

    const newOtp = [...otp];
    let i = index;
    for (let d of digits) {
      if (i >= newOtp.length) break;
      newOtp[i] = d;
      i++;
    }
    setOtp(newOtp);

    const nextIndex = Math.min(i, newOtp.length - 1);
    const nextInput = document.getElementById(`otp-input-${nextIndex}`);
    if (nextInput) nextInput.focus();
  };

  const handleOtpKeyDown = (e, index) => {
    const key = e.key;
    if (key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];
      if (newOtp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        const prev = document.getElementById(`otp-input-${index - 1}`);
        if (prev) prev.focus();
      }
    } else if (key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      const prev = document.getElementById(`otp-input-${index - 1}`);
      if (prev) prev.focus();
    } else if (key === 'ArrowRight' && index < otp.length - 1) {
      e.preventDefault();
      const next = document.getElementById(`otp-input-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = (e.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!pasteData) return;
    const newOtp = [...otp];
    for (let i = 0; i < Math.min(pasteData.length, otp.length); i++) {
      newOtp[i] = pasteData[i];
    }
    setOtp(newOtp);
    const focusIndex = Math.min(pasteData.length, otp.length - 1);
    const nextInput = document.getElementById(`otp-input-${focusIndex}`);
    if (nextInput) nextInput.focus();
  };

  const handleMarketingCheckout = async () => {
    setIsProcessingCheckout(true);
    try {
      const planData = findPlanProduct(products, planName, subscriptionMode);
      
      if (!planData) {
        throw new Error('Plan not found');
      }

      const { product, priceInterval, intervalType } = planData;

      // For Guided ($0) plan, create direct subscription
      if (priceInterval.unit_amount === 0) {
        const profile = JSON.parse(localStorage.getItem('profile') || '{}');
        const userId = profile._id || profile.id;

        const subscriptionPayload = {
          user_id: userId,
          plan_id: product.id,
          price_id: priceInterval.stripe_price_id,
          billing_cycle: intervalType,
          metadata: {
            source: 'marketing_page',
            plan_name: planName,
            subscription_mode: subscriptionMode
          }
        };

        await createDirectSubscription(subscriptionPayload);
        
        // Navigate to QR page with plan info
        const queryString = searchParams.toString();
        navigate(`/login/qr${queryString ? '?' + queryString : ''}`);
        return;
      }

      const profile = JSON.parse(localStorage.getItem('profile') || '{}');
      const userId = profile._id || profile.id;

      const checkoutPayload = {
        user_id: userId,
        plan_id: product.id,
        price_id: priceInterval.stripe_price_id,
        billing_cycle: intervalType,
        success_url: `${window.location.origin}/login/payment-success?plan=${planName}&subscription_mode=${subscriptionMode}&session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/login?payment=cancel`,
        metadata: {
          source: 'marketing_page',
          plan_name: planName,
          subscription_mode: subscriptionMode
        }
      };

      const checkoutResponse = await startSubscription(checkoutPayload);
      
      // Handle new response format
      if (checkoutResponse?.status === 'success' && checkoutResponse?.payload?.url) {
        window.location.href = checkoutResponse.payload.url;
      } else if (checkoutResponse?.url) {
        // Fallback for old format
        window.location.href = checkoutResponse.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMsg = err.message || 'Failed to process checkout. Please try again.';
      setError(errorMsg);
      setIsProcessingCheckout(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (isSending) return;

    setError('');
    if (otp.join('').length !== 6) {
      setError('Please enter a valid 6-digit OTP.');
      return;
    }

    setIsSending(true);

    try {
      let response;
      
      if (authData.authMethod === 'email') {
        response = await verifyEmailSignInOtp(authData.email, otp.join(''), shareToken);
      } else {
        response = await verifySignupPhone(authData.countryCode, authData.contact, otp.join(''), shareToken);
      }
      
      if (response.status === 200) {
        // Store tokens
        if (response.payload?.idToken) {
          Cookies.set('idtoken', response.payload.idToken, { secure: true });
        }
        if (response.payload?.refreshToken) {
          Cookies.set('refreshtoken', response.payload.refreshToken, { secure: true });
        }
        if (response.payload?.localId) {
          Cookies.set('localId', response.payload.localId);
        }

        // Update profile data and fetch fresh profile from API
        if (addProfileData) {
          await addProfileData();
        }

        // Fetch fresh profile from API to get accurate onboarding status
        let profile = {};
        try {
          const freshProfile = await getUserProfile();
          if (freshProfile) {
            profile = freshProfile;
            localStorage.setItem('profile', JSON.stringify(freshProfile));
          }
        } catch (err) {
          console.error('Error fetching profile:', err);
          // Fallback to localStorage if API fails
          profile = JSON.parse(localStorage.getItem('profile') || '{}');
        }

        // Check onboarding status from profile
        const onboardingSteps = profile?.onboarding_step || [];
        const hasCompletedOnboarding = Array.isArray(onboardingSteps) && 
          onboardingSteps.some(s => String(s).toLowerCase() === 'completed');
        const hasSubscription = Array.isArray(onboardingSteps) && 
          onboardingSteps.some(s => String(s).toLowerCase() === 'subscription');
        const isVerified = profile?.is_verified !== false;

        // Handle new user or unverified user flow
        if (authData.isNewUser || !isVerified || !hasCompletedOnboarding || !hasSubscription) {
          if (planName && subscriptionMode && products.length > 0) {
            await handleMarketingCheckout();
          } else {
            // Navigate to QR page for new users
            const queryString = searchParams.toString();
            navigate(`/login/qr${queryString ? '?' + queryString : ''}`);
          }
        } else {
          // Existing verified user with completed onboarding - normal login flow
          await handlePostLoginFlow({ navigate, addProfileData });
        }
      } else {
        const errorMsg = response.message || 'Invalid OTP. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to verify OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleResendOtp = async () => {
    if (isTimerActive || isSending) return;
    
    setError('');
    setIsSending(true);

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let response;
      
      if (authData.authMethod === 'email') {
        response = await authenticateWithEmail(authData.email.toLowerCase(), timezone);
      } else {
        response = await authenticateWithPhone(authData.contact, authData.countryCode, timezone);
      }
      
      if (response.status === 200) {
        setTimer(60);
        setIsTimerActive(true);
      } else {
        const errorMsg = response.message || 'Failed to resend OTP. Please try again.';
        setError(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to resend OTP. Please try again.';
      setError(errorMsg);
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    const queryString = searchParams.toString();
    const route = authData?.authMethod === 'email' ? '/login/email' : '/login/phone';
    navigate(`${route}${queryString ? '?' + queryString : ''}`);
  };

  if (!authData) {
    return null;
  }

  const displayValue = authData.authMethod === 'email' ? authData.email : authData.phoneNumber;

  return (
    <AuthLayout showBackButton={false} onBack={handleBack}>
      {planInfo && (
        <p className="auth-plan-info">
          You're getting: {planInfo.name} - ${planInfo.price}/{planInfo.interval}
        </p>
      )}
      
      <form onSubmit={handleVerifyOtp} className="auth-form">
        <h2 className="auth-title">Verify your {authData.authMethod === 'email' ? 'email' : 'phone'}</h2>
        <p className="auth-subtitle">
          We sent a six-digit confirmation code to <strong>{displayValue}</strong>
        </p>

        <div className="otp-container">
          {otp.map((value, index) => (
            <input
              key={index}
              id={`otp-input-${index}`}
              type="text"
              maxLength={index === 0 ? otp.length : 1}
              value={value}
              autoFocus={index === 0}
              onChange={(e) => handleOtpChange(e.target.value, index)}
              onKeyDown={(e) => handleOtpKeyDown(e, index)}
              onPaste={handleOtpPaste}
              inputMode="numeric"
              pattern="[0-9]*"
              {...(index === 0 ? { autoComplete: 'one-time-code', name: 'one-time-code' } : {})}
              className="otp-input"
            />
          ))}
        </div>

        <div className="resend-container">
          <span className="resend-text">Didn't get code?</span>
          {isTimerActive ? (
            <span className="resend-timer">Resend code in {timer}s</span>
          ) : (
            <button
              type="button"
              className="resend-button"
              onClick={handleResendOtp}
              disabled={isSending}
            >
              Resend code
            </button>
          )}
        </div>

        <InlineErrorMessage message={error} />

        <button
          type="submit"
          className={`auth-submit-button ${otp.join('').length === 6 && !isSending && !isProcessingCheckout ? "enabled" : ""}`}
          disabled={otp.join('').length !== 6 || isSending || isProcessingCheckout}
        >
          {isProcessingCheckout ? 'Processing...' : isSending ? 'Verifying...' : 'Verify'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default OtpVerificationPage;
